import { ConnectionStatus } from '@/components/integrations/wordpress/ConnectionStatus'
import { HowItWorks } from '@/components/integrations/wordpress/HowItWorks'
import { PageHeader } from '@/components/integrations/wordpress/PageHeader'
import {
  WordPressConfigForm,
  wordpressIntegrationSchema,
  type WordpressIntegrationSchema,
} from '@/components/integrations/wordpress/WordPressConfigForm'
import { orpc } from '@/lib/orpc/client'
import { queryClient } from '@/lib/query'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'

export const Route = createFileRoute(
  '/_dashboard/settings/integrations/wordpress',
)({
  component: RouteComponent,
  validateSearch: z.object({
    code: z.string().optional(),
    state: z.string().optional(),
  }),
  beforeLoad: async ({ search }) => {
    if (search.code && search.state) {
      const expectedState = sessionStorage.getItem('wordpress_oauth_state')
      if (expectedState) {
        await orpc.integrations.wordpress.exchangeWordpressToken.call({
          code: search.code,
          state: search.state,
          expectedState,
        })
        sessionStorage.removeItem('wordpress_oauth_state')
      } else {
        toast.error('Invalid OAuth state', {
          description: 'Please try connecting again.',
        })
      }
      throw redirect({
        to: '/settings/integrations/wordpress',
        search: {},
        replace: true,
      })
    }

    await queryClient.ensureQueryData(
      orpc.integrations.getIntegrations.queryOptions(),
    )
  },
})

function RouteComponent() {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const integrationsQuery = useQuery(
    orpc.integrations.getIntegrations.queryOptions({
      select: (data) => {
        const wordpressIntegration = data.find((integration) => {
          return integration.config.type === 'wordpress'
        })
        if (wordpressIntegration?.config.type === 'wordpress') {
          return {
            ...wordpressIntegration,
            config: wordpressIntegration.config,
          }
        }
        return null
      },
    }),
  )

  const form = useForm<WordpressIntegrationSchema>({
    resolver: zodResolver(wordpressIntegrationSchema),
    defaultValues: {
      blogUrl: integrationsQuery.data?.config.blogUrl || '',
    },
  })

  // Update form when integration data loads
  useEffect(() => {
    if (integrationsQuery.data?.config.blogUrl) {
      form.setValue('blogUrl', integrationsQuery.data.config.blogUrl)
    }
  }, [integrationsQuery.data, form])

  const toggleEnabledMutation = useMutation(
    orpc.integrations.toggleIntegrationEnabled.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
        toast.success('Integration updated successfully')
      },
      onError: () => {
        toast.error('Failed to update integration')
      },
    }),
  )

  const disconnectMutation = useMutation(
    orpc.integrations.deleteIntegration.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: orpc.integrations.getIntegrations.queryKey(),
        })
        toast.success('WordPress integration disconnected successfully')
        form.reset({ blogUrl: '' })
      },
      onError: () => {
        toast.error('Failed to disconnect WordPress integration')
      },
    }),
  )

  const handleToggleEnabled = (checked: boolean) => {
    if (!integrationsQuery.data?.id) return
    toggleEnabledMutation.mutate({
      integrationId: integrationsQuery.data.id,
      enabled: checked,
    })
  }

  const handleDisconnect = () => {
    if (!integrationsQuery.data?.id) return
    if (
      confirm('Are you sure you want to disconnect your WordPress integration?')
    ) {
      disconnectMutation.mutate({
        integrationId: integrationsQuery.data.id,
      })
    }
  }

  const isWordpressConnected = !!integrationsQuery.data

  const startOAuthMutation = useMutation(
    orpc.integrations.wordpress.startWordpressOAuth.mutationOptions({
      onSuccess: (result) => {
        sessionStorage.setItem('wordpress_oauth_state', result.state)
        window.location.href = result.url
      },
      onError: () => {
        toast.error('Failed to start WordPress authentication', {
          description: 'Please try again or contact support.',
        })
      },
    }),
  )

  const onSubmit = async (data: WordpressIntegrationSchema) => {
    setIsSubmitting(true)
    try {
      await startOAuthMutation.mutateAsync({
        blogUrl: data.blogUrl,
      })
    } catch (error) {
      console.error('Failed to start OAuth:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="max-w-4xl">
      <PageHeader />

      <ConnectionStatus isConnected={isWordpressConnected} />

      {isWordpressConnected && <div className="mb-6" />}

      <WordPressConfigForm
        form={form}
        onSubmit={onSubmit}
        isSubmitting={isSubmitting}
        isWordpressConnected={isWordpressConnected}
        integrationData={
          integrationsQuery.data
            ? {
                id: integrationsQuery.data.id,
                enabled: integrationsQuery.data.enabled,
              }
            : null
        }
        onDisconnect={handleDisconnect}
        onToggleEnabled={handleToggleEnabled}
        isDisconnecting={disconnectMutation.isPending}
        isTogglingEnabled={toggleEnabledMutation.isPending}
      />

      <div className="mt-6">
        <HowItWorks />
      </div>
    </div>
  )
}
